// app/login/page.js
'use client';

import { useState, useEffect, useRef } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { useRouter } from 'next/navigation';
import LoginForm from '@/components/auth/LoginForm';
import TwoFactorModal from '@/components/auth/TwoFactorModal';
import PasswordResetModal from '@/components/auth/PasswordResetModal';
import CreateAccountModal from '@/components/auth/CreateAccountModal';
import { APP_VERSION_LABEL } from '@/utils/appVersion';
import { signedInHome } from '@/utils/authRouting';

export default function LoginPage() {
    const router = useRouter();
    const { isAuthenticated, isAuthenticating, currentOrg, loading } = useAuth();

    const [showTwoFactor, setShowTwoFactor] = useState(false);
    const [showPasswordReset, setShowPasswordReset] = useState(false);
    const [showCreateAccount, setShowCreateAccount] = useState(false);
    const [twoFactorData, setTwoFactorData] = useState(null);

    // Arrived already signed in — go straight to the right place. This used to
    // push '/' for everyone, and a user with no organization then watched
    // AuthGuard replace that with /memberships (audit 12t item 12).
    // `signedInHome` is the rule AuthGuard applies, so both agree on the
    // target; `replace`, not `push`, so /login does not stay in history.
    //
    // Only for a user who was ALREADY authenticated when the page settled
    // (`arrivedSignedIn`, decided once `loading` clears). A sign-in performed
    // on this page is routed by AuthContext itself, by account type
    // (/househome, /trainingcalshared, …), and a page-level redirect firing
    // when `isAuthenticated` flips would race it.
    const arrivedSignedIn = useRef(null);
    useEffect(() => {
        if (loading) return;
        if (arrivedSignedIn.current === null) arrivedSignedIn.current = isAuthenticated;
        if (isAuthenticated && arrivedSignedIn.current) {
            router.replace(signedInHome(currentOrg));
        }
    }, [loading, isAuthenticated, currentOrg, router]);

    const handleLoginSuccess = (data) => {
        if (data.twoFactorRequired) {
            setTwoFactorData({
                method: data.twoFactorMethod,
                availableMethods: data.availableMethods
            });
            setShowTwoFactor(true);
        }
    };

    // Top-aligned on phones, centred from `sm` up.
    //
    // iOS raises its password-autofill sheet over the bottom of the viewport,
    // and with the card centred that sheet covered "Click to Create Your
    // Account" and "Forgot password?" — the two links a user who cannot sign in
    // actually needs. Pinning the card to the top keeps them above the sheet.
    // PM request, 2026-08-09.
    //
    // `min-h-dvh`, not `min-h-screen`: `vh` is the LARGE viewport on a phone, so
    // the container would exceed the visible area — the same trap as the sidebar
    // (see .sidebar-panel in globals.css). NOTE the dark-theme rules in
    // globals.css key on `.min-h-screen.bg-gray-50`; the dvh variants are listed
    // there too, or this page loses its dark background.
    return (
        <div className="min-h-dvh flex items-start sm:items-center justify-center bg-gray-50 px-4 pt-6 pb-8 sm:py-8">
            <div className="max-w-md w-full">
                <div className="bg-white rounded-lg shadow-lg p-6 sm:p-8">
                    {/* Logo */}
                    <div className="text-center mb-6 sm:mb-8">
                        {/* eslint-disable-next-line @next/next/no-img-element */}
                        <img
                            src="/fse_logo.png"
                            alt="FireSync"
                            /* `w-88` forced 22rem — wider than a 320px phone, and it
                               squashed the logo against the fixed h-16. */
                            className="h-12 sm:h-16 w-auto max-w-full mx-auto mb-1.5"
                        />
                        {/*<h1 className="text-2xl font-aldrich">
                            FireSync <span className="font-bold">PORTAL</span>
                        </h1>*/}
                        {/* Nameplate, directly beneath the logo (PM, 2026-08-09).
                            Styled in globals.css as .login-plate — see the note
                            there on why the chamfer needs two clipped layers. */}
                        <div className="login-plate">
                            <span>FireSync Admin Portal</span>
                        </div>

                        <p className="text-sm text-gray-600 mt-3">{APP_VERSION_LABEL}</p>
                    </div>

                    {/* Login Form */}
                    <LoginForm
                        onSuccess={handleLoginSuccess}
                        onPasswordReset={() => setShowPasswordReset(true)}
                        onCreateAccount={() => setShowCreateAccount(true)}
                    />

                    {/* Loading indicator */}
                    {isAuthenticating && (
                        <div className="text-center mt-4">
                            <div className="inline-flex items-center">
                                <svg className="animate-spin h-5 w-5 mr-3 text-gray-600" viewBox="0 0 24 24">
                                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                                </svg>
                                <span className="text-gray-600">Authenticating...</span>
                            </div>
                        </div>
                    )}
                </div>

                {/* Links */}
                {/* Legacy Login.razor L45: the prompt sits directly above the
                    create button (audit 12t item 8). */}
                <div className="mt-4 text-center">
                    <p className="text-sm text-gray-600">
                        Creating an account to join an existing organization?
                    </p>
                    <button
                        onClick={() => setShowCreateAccount(true)}
                        className="text-blue-600 hover:text-blue-800 font-semibold"
                    >
                        Click to Create Your Account
                    </button>
                </div>

                <div className="mt-4 text-center">
                    <p className="text-sm text-gray-600">
                        Forgot password?{' '}
                        <button
                            onClick={() => setShowPasswordReset(true)}
                            className="text-blue-600 hover:text-blue-800"
                        >
                            Click to Reset
                        </button>
                    </p>
                </div>
            </div>

            {/* Modals */}
            {showTwoFactor && (
                <TwoFactorModal
                    isOpen={showTwoFactor}
                    onClose={() => setShowTwoFactor(false)}
                    method={twoFactorData?.method}
                    availableMethods={twoFactorData?.availableMethods}
                />
            )}

            {showPasswordReset && (
                <PasswordResetModal
                    isOpen={showPasswordReset}
                    onClose={() => setShowPasswordReset(false)}
                />
            )}

            {showCreateAccount && (
                <CreateAccountModal
                    isOpen={showCreateAccount}
                    onClose={() => setShowCreateAccount(false)}
                />
            )}
        </div>
    );
}